import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { toast } from "../stores/toast";
import { Spinner } from "./ui";

interface ImageUploadFieldProps {
  label?: string;
  hint?: string;
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
}

export function ImageUploadField({ label, hint, value, onChange, max = 5 }: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function uploadFiles(files: FileList | File[]) {
    const list = [...files];
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast("error", `Maximum ${max} images allowed`);
      return;
    }
    const batch = list.slice(0, remaining);
    if (list.length > remaining) {
      toast("error", `Only ${remaining} more image${remaining === 1 ? "" : "s"} can be added`);
    }

    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of batch) formData.append("files", file);
      const res = await api<{ urls: string[] }>("/uploads/public", { formData });
      onChange([...value, ...res.urls].slice(0, max));
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      {label && <p className="text-xs font-medium text-white/70">{label}</p>}
      {hint && <p className="text-[11px] text-white/40">{hint}</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((url, index) => (
            <div key={`${url}-${index}`} className="relative h-16 w-16 overflow-hidden rounded-lg border border-line">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-white/80 hover:text-white"
                onClick={() => removeAt(index)}
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {value.length < max && (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length) void uploadFiles(e.dataTransfer.files);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white/[0.02] px-4 py-6 text-xs text-white/50 transition hover:border-brand-pink/40 hover:text-white/70 disabled:opacity-50"
        >
          {uploading ? <Spinner className="h-4 w-4" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : `Upload images (${value.length}/${max})`}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void uploadFiles(e.target.files);
        }}
      />
    </div>
  );
}
